
import React, { useEffect, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/domains/auth/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import ProfileForm from '@/components/dashboard/ProfileForm';
import { User } from '@supabase/supabase-js';
import { Profile, PortfolioItem, ExperienceItem, PortfolioImage } from '@/hooks/profile';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Json } from '@/integrations/supabase/types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const AdminEditProfile = () => {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const { profile: adminProfile, loading: adminProfileLoading } = useProfile(user?.id);
  const [targetProfile, setTargetProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string>('');
  const [emailUpdating, setEmailUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Default placeholder image
  const defaultAvatar = '/lovable-uploads/default-profile-avatar.png';

  useEffect(() => {
    if (!id) return;

    const fetchTargetProfile = async () => {
      try {
        setLoading(true);
        
        // First fetch the user email from auth.users
        const { data: userData, error: userError } = await supabase.auth.admin.getUserById(id);
        
        if (userError) {
          console.error('Error fetching user data:', userError);
        } else if (userData?.user) {
          setUserEmail(userData.user.email || '');
        }
        
        // Then fetch the profile data
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          throw error;
        }

        // Transform portfolio items to make them compatible with the PortfolioItem type
        const portfolioItems = (data.portfolio_items as Json[] || []).map(item => {
          const portfolioItem: PortfolioItem = {
            id: (item as any)?.id || crypto.randomUUID(),
            title: (item as any)?.title || '',
            description: (item as any)?.description || '',
            image_url: (item as any)?.image_url,
            url: (item as any)?.url,
            tags: (item as any)?.tags || [],
            created_at: (item as any)?.created_at,
            // Initialize empty images array that will be populated if images exist
            images: Array.isArray((item as any)?.images) 
              ? (item as any).images.map((img: any, index: number) => ({
                  id: img.id || `legacy-${index}`,
                  url: img.url || '',
                  caption: img.caption || null,
                  order: img.order !== undefined ? img.order : index,
                  isLegacy: img.isLegacy || false
                }))
              : []
          };
          return portfolioItem;
        });

        // Transform experience items
        const experienceItems = (data.experience_items as Json[] || []).map(item => {
          const experienceItem: ExperienceItem = {
            id: (item as any)?.id || crypto.randomUUID(),
            position: (item as any)?.position || (item as any)?.title || '',
            company: (item as any)?.company || '',
            start_date: (item as any)?.start_date || '',
            end_date: (item as any)?.end_date,
            current: (item as any)?.current || false,
            description: (item as any)?.description,
            location: (item as any)?.location
          };
          return experienceItem;
        });

        setTargetProfile({
          username: data.username || '',
          location: data.location || '',
          profession: data.profession || '',
          avatar_url: data.avatar_url,
          is_admin: data.is_admin || false,
          bio: data.bio || '',
          years_experience: data.years_experience || 0,
          skills: data.skills || [],
          education: data.education || '',
          languages: data.languages || [],
          portfolio_url: data.portfolio_url || '',
          instagram_username: data.instagram_username || '',
          linkedin_username: data.linkedin_username || '',
          portfolio_items: portfolioItems,
          experience_items: experienceItems
        });
      } catch (error: any) {
        console.error('Error fetching profile:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to load user profile",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTargetProfile();
  }, [id, toast]);

  const handleProfileUpdate = (updatedProfile: Profile) => {
    setTargetProfile(updatedProfile);
    toast({
      title: "Profile updated",
      description: "User profile has been successfully updated",
    });
  };

  const handleEmailUpdate = async () => {
    if (!id || !userEmail.trim()) return;
    
    try {
      setEmailUpdating(true);
      
      // Update the user's email in auth.users
      const { error } = await supabase.auth.admin.updateUserById(
        id,
        { email: userEmail }
      );
      
      if (error) throw error;
      
      toast({
        title: "Email updated",
        description: "User email has been successfully updated",
      });
    } catch (error: any) {
      console.error('Error updating email:', error);
      toast({
        title: "Error updating email",
        description: error.message || "Failed to update user email",
        variant: "destructive",
      });
    } finally {
      setEmailUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!id) return;
    
    try {
      setIsDeleting(true);
      
      // Delete the user from auth.users (this will cascade delete their profile)
      const { error } = await supabase.auth.admin.deleteUser(id);
      
      if (error) {
        // If permission error, try using a different approach
        if (error.message?.includes('permission denied')) {
          // Try to delete just the profile which might be all we need
          const { error: profileError } = await supabase
            .from('profiles')
            .delete()
            .eq('id', id);
            
          if (profileError) throw profileError;
          
          toast({
            title: "Notice",
            description: "User profile removed. The authentication record may require manual deletion in Supabase.",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "User deleted",
          description: "User has been successfully deleted",
        });
      }
      
      // Navigate back to admin dashboard
      navigate('/admin');
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error deleting user",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Mock user object for the ProfileForm
  const mockUser: User = {
    id: id || '',
    app_metadata: {},
    user_metadata: {},
    aud: '',
    created_at: '',
  };

  // Get initials for avatar fallback
  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (authLoading || adminProfileLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect if not logged in or not an admin
  if (!user || !adminProfile.is_admin) {
    return <Navigate to="/" replace />;
  }

  if (!targetProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>User not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Admin Dashboard
          </Button>
          
          <Button 
            variant="destructive" 
            onClick={() => setDeleteDialogOpen(true)}
            className="flex items-center gap-2"
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4" />
            Delete User
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={targetProfile.avatar_url || defaultAvatar} alt={targetProfile.username || 'User'} />
              <AvatarFallback>{getInitials(targetProfile.username)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <CardTitle>Admin Controls</CardTitle>
              <CardDescription>
                Advanced settings only available to admins
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-1">
              <div className="space-y-2">
                <Label htmlFor="userEmail">User Email</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="userEmail"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    placeholder="Email address"
                    type="email"
                    disabled={emailUpdating}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    onClick={handleEmailUpdate}
                    disabled={emailUpdating || !userEmail.trim()}
                  >
                    {emailUpdating ? "Updating..." : "Update Email"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={targetProfile.avatar_url || defaultAvatar} alt={targetProfile.username || 'User'} />
              <AvatarFallback>{getInitials(targetProfile.username)}</AvatarFallback>
            </Avatar>
            <div>
              <CardTitle>Edit User Profile</CardTitle>
              <CardDescription>
                You are editing the profile for {targetProfile.username || 'Anonymous User'}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <ProfileForm 
              user={user} 
              initialProfile={targetProfile} 
              onProfileUpdate={handleProfileUpdate}
              targetUserId={id}
            />
          </CardContent>
        </Card>
      </div>
      
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm User Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {targetProfile.username || 'this user'}? This action cannot be undone and will remove all user data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex space-x-2 py-3">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEditProfile;
